import { useEffect, useMemo, useRef } from 'react';

import {
  OrderAheadProvider,
  useCreateOrderAheadSessionMutation,
  OrderAheadCreateSessionPayloadFragment,
} from '@ocx/graphql';
import { logInDev } from '@ocx/utils';

const typenameToProviderMap: Record<OrderAheadProvider, OrderAheadCreateSessionPayloadFragment['__typename']> = {
  [OrderAheadProvider.Olo]: 'OloOrderAheadCreateSessionPayload',
  [OrderAheadProvider.Lula]: 'LulaOrderAheadCreateSessionPayload',
};

// This hook is used for Order Ahead provides that require creating a session to get the iframe URL, like Lula and Olo.
export const useIframeUrl = (params: { provider: OrderAheadProvider }) => {
  const isRequestTriggeredRef = useRef(false);
  const [createOrderAheadSession, { loading, error, data }] = useCreateOrderAheadSessionMutation({
    variables: { provider: params.provider },
  });

  useEffect(() => {
    if (isRequestTriggeredRef.current) return;
    isRequestTriggeredRef.current = true;
    createOrderAheadSession().catch(logInDev);
  }, [createOrderAheadSession]);

  const iframeUrl = useMemo(() => {
    if (!data) return null;
    if (data.result.__typename !== typenameToProviderMap[params.provider]) return null;

    // TODO resolve schema issue, in generated schema iframeUrl is any
    if (typeof data.result.iframeUrl !== 'string') {
      logInDev('Invalid iframeUrl returned from createOrderAheadSession: expected string');
      return null;
    }
    return data.result.iframeUrl satisfies string;
  }, [data, params.provider]);

  return useMemo(
    () => ({
      iframeUrl,
      error,
      isLoading: loading,
    }),
    [error, iframeUrl, loading],
  );
};
